import posthog, { type ConfigDefaults, type PostHog } from 'posthog-js';
import {
  capturePostHogEvent as captureEvent,
  getPostHogAppName,
  identifyPostHogUser as identifyUser,
} from './posthogUtils';

const client = import.meta.env.VITE_CLIENT;
const stage = import.meta.env.VITE_STAGE;
const posthogToken = import.meta.env.VITE_POSTHOG_TOKEN;
const apiHost = import.meta.env.VITE_POSTHOG_API_HOST;
const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST;

/**
 * The latest config default from PostHog. This option is used by legacy systems to prevent breaking changes.
 * For our purposes, we can use the latest config default.
 */
const LATEST_CONFIG_DEFAULT: ConfigDefaults = '2026-01-30';

/**
 * import.meta.env.PROD is true when we are running on AWS, does not mean prod stage.
 * VITE_ENABLE_POSTHOG covers deployed environments; VITE_ENABLE_POSTHOG_LOCAL enables local dev.
 */
export const isPostHogEnabled =
  Boolean(posthogToken) &&
  Boolean(apiHost) &&
  Boolean(uiHost) &&
  ((import.meta.env.PROD && import.meta.env.VITE_ENABLE_POSTHOG === 'true') ||
    import.meta.env.VITE_ENABLE_POSTHOG_LOCAL === 'true');

export let posthogInstance: PostHog | null = null;

if (isPostHogEnabled) {
  /**
   * Initialise PostHog with the following configuration:
   * @see https://posthog.com/docs/libraries/js/config
   */
  posthog.init(posthogToken!, {
    api_host: apiHost,
    // Required when api_host is a reverse proxy so toolbar / session replay UI still work.
    ...(uiHost ? { ui_host: uiHost } : {}),
    defaults: LATEST_CONFIG_DEFAULT,
    person_profiles: 'identified_only',
    // Page views and interactions are tracked manually via useTrackPageView / capturePostHogEvent.
    capture_pageview: false,
    autocapture: false,
  });

  posthog.register({
    client,
    stage,
    appName: getPostHogAppName(client, stage),
  });

  posthogInstance = posthog;
  console.info('PostHog initialized');
}

export function capturePostHogEvent(eventName: string, properties?: Record<string, unknown>): void {
  captureEvent(posthogInstance, isPostHogEnabled, eventName, properties);
}

export function identifyPostHogUser(userId: string, traits?: Record<string, unknown>): void {
  identifyUser(posthogInstance, isPostHogEnabled, userId, traits);
}
